import { SerializedConstructor } from '@langchain/core/load/serializable';
import { FormControl, FormLabel } from '@mui/joy';

import {
  CHAT_MODEL_MAPPING,
  getModelDisplayNameFromIdList,
} from '@/Pages/Playground/Playground.constants';
import { cn } from '@/utils/tailwind';

import { InstructModelBadge } from '../../InstructModelBadge';
import {
  ManifestEditorProps,
  ManifestPreviewProps,
  tableVariantContainerClass,
  tableVariantLabelClass,
  tableVariantRowClass,
} from '../../Manifest.utils';
import { ManifestNavigation } from '../../ManifestNavigation';
import { ManifestSelectModel } from '../../ManifestSelectModel';
import { ManifestProviderReplaceWithSwitch } from '../../ManifestSelectProvider';
import { ManifestSlider } from '../../ManifestSlider';
import { ManifestStopEditor } from '../../ManifestStopEditor';
import { BedrockCommonRegionDropdown } from '../common/ManifestCommonBedrock';
import { DEFAULT_MODEL, MODELS } from './ManifestBedrock.utils';

export function ManifestBedrock(
  props: ManifestEditorProps<SerializedConstructor>
) {
  const kwargs = props.value.kwargs ?? {};

  return (
    <>
      <ManifestNavigation navigation={props.navigation} />

      <div
        className={cn(
          'flex flex-col gap-3',
          props.variant === 'table' && tableVariantContainerClass
        )}
      >
        <ManifestProviderReplaceWithSwitch
          variant={props.variant}
          onChange={props.onChange}
          value={props.value}
          readOnly={props.readOnly}
          isChatModel={false}
          otherModelId={CHAT_MODEL_MAPPING['ChatBedrock'].modelId}
        />

        <ManifestSelectModel
          models={MODELS}
          onChange={(model_id) => {
            props.onChange({
              ...props.value,
              kwargs: { ...kwargs, model_id },
            });
          }}
          value={kwargs.model_id || DEFAULT_MODEL}
          readOnly={props.readOnly}
          variant={props.variant}
        />

        <ManifestSlider
          title="Temperature"
          value={kwargs.temperature}
          min={0}
          max={2}
          step={0.1}
          onChange={(temperature) =>
            props.onChange({
              ...props.value,
              kwargs: { ...kwargs, temperature },
            })
          }
          readOnly={props.readOnly}
          variant={props.variant}
        />

        <ManifestSlider
          title="Max Tokens"
          value={kwargs.max_tokens == null ? -1 : kwargs.max_tokens}
          min={-1}
          max={8000}
          step={1}
          onChange={(max_tokens) =>
            props.onChange({
              ...props.value,
              kwargs: {
                ...kwargs,
                max_tokens: max_tokens === -1 ? null : max_tokens,
              },
            })
          }
          readOnly={props.readOnly}
          variant={props.variant}
        />

        {props.variant === 'table' && props.readOnly ? (
          <div className={tableVariantRowClass}>
            <div className={tableVariantLabelClass}>Region</div>
            <div>{props.value.kwargs['region_name'] ?? ''}</div>
          </div>
        ) : (
          <FormControl>
            <FormLabel>Region</FormLabel>
            <BedrockCommonRegionDropdown
              regionName={kwargs.region_name}
              onChange={(region_name) =>
                props.onChange({
                  ...props.value,
                  kwargs: { ...kwargs, region_name },
                })
              }
            />
          </FormControl>
        )}
      </div>

      {props.options && (!props.readOnly || !!props.options.stop) && (
        <div className="flex flex-col gap-2">
          <FormLabel>Stop Sequences</FormLabel>
          <ManifestStopEditor
            value={props.options.stop}
            onChange={(stop) =>
              props.onOptionsChange({
                ...props.options,
                stop,
              })
            }
            readOnly={props.readOnly}
          />
        </div>
      )}
    </>
  );
}

export function ManifestBedrockPreview(
  props: ManifestPreviewProps<SerializedConstructor>
) {
  const displayLabel = getModelDisplayNameFromIdList(props.value.id);
  const kwargs = props.value.kwargs;
  const model = kwargs.model || DEFAULT_MODEL;

  if (props.variant === 'line') {
    return (
      <div className="flex flex-row items-center gap-1">
        <span className="font-semibold">{displayLabel}</span>
        <span>{model}</span>
        <InstructModelBadge />
      </div>
    );
  }

  return null;
}
